import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  type DragStartEvent,
  type DragOverEvent,
  type DragEndEvent,
  type DragCancelEvent,
} from '@dnd-kit/core';
import type { Task } from '../../hooks/queries/useTasks';
import { useEffectiveTaskFields } from '../../hooks/useEffectiveTaskFields';
import { getPriorityMeta, getDueDate, getUrgencyMeta } from './taskHelpers';
import { Popover, PopoverTrigger, PopoverContent, PopoverAnchor } from '../ui/popover';

interface TasksCalendarViewProps {
  tasks: Task[];
  onOpenTask?: (task: Task) => void;
  onReschedule?: (task: Task, isoDay: string) => void;
  onQuickCreate?: (isoDay: string, title: string) => void;
}

const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const NAV_DWELL_MS = 600;

function startOfMonthMonday(date: Date): Date {
  const first = new Date(date.getFullYear(), date.getMonth(), 1);
  // JS getDay : 0 = dimanche
  const day = first.getDay();
  const offset = day === 0 ? 6 : day - 1; // décale lundi=0
  first.setDate(first.getDate() - offset);
  first.setHours(0, 0, 0, 0);
  return first;
}

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function sameMonth(a: Date, b: Date): boolean {
  return a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();
}

function sameDay(a: Date, b: Date): boolean {
  return isoDate(a) === isoDate(b);
}

export function TasksCalendarView({ tasks, onOpenTask, onReschedule, onQuickCreate }: TasksCalendarViewProps) {
  const { priority: showPriority } = useEffectiveTaskFields();
  const [cursor, setCursor] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const [activeTask, setActiveTask] = useState<Task | null>(null);
  const [quickCreateDay, setQuickCreateDay] = useState<string | null>(null);
  const [quickTitle, setQuickTitle] = useState('');
  const navTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // dnd-kit ne redéclenche onDragOver que si `over` CHANGE : rester immobile
  // sur un chevron ne renvoie plus l'event, donc on garde la dernière cible
  // connue pour pouvoir réarmer le timer nous-mêmes à chaque tick.
  const lastOverIdRef = useRef<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  // Nettoie le timer de navigation au démontage (drag interrompu par un
  // changement de vue par exemple).
  useEffect(() => {
    return () => {
      if (navTimer.current) clearTimeout(navTimer.current);
    };
  }, []);

  // Index tasks by date string (yyyy-mm-dd)
  const tasksByDate = useMemo(() => {
    const map = new Map<string, Task[]>();
    for (const t of tasks) {
      const due = getDueDate(t);
      if (!due) continue;
      // Normalise sur la date (sans heure)
      const d = new Date(due);
      if (Number.isNaN(d.getTime())) continue;
      const key = isoDate(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
      const arr = map.get(key) ?? [];
      arr.push(t);
      map.set(key, arr);
    }
    return map;
  }, [tasks]);

  const days = useMemo(() => {
    const start = startOfMonthMonday(cursor);
    const result: Date[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      result.push(d);
    }
    return result;
  }, [cursor]);

  const today = new Date();
  const monthLabel = cursor.toLocaleDateString('fr-FR', {
    month: 'long',
    year: 'numeric',
  });

  function goPrev() {
    setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
  }
  function goNext() {
    setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
  }
  function goToday() {
    const now = new Date();
    setCursor(new Date(now.getFullYear(), now.getMonth(), 1));
  }

  function clearNavTimer() {
    if (navTimer.current) {
      clearTimeout(navTimer.current);
      navTimer.current = null;
    }
  }

  function handleDragStart(e: DragStartEvent) {
    const task = tasks.find((t) => t.id === String(e.active.id));
    setActiveTask(task ?? null);
  }

  // Arme (ou réarme) le timer de dwell sur un chevron. Rappelée par elle-même
  // tant que le pointeur reste sur la même cible, puisque onDragOver ne se
  // redéclenche pas sans changement de `over` (voir lastOverIdRef ci-dessus).
  function armNavTimer(overId: 'nav:prev' | 'nav:next') {
    navTimer.current = setTimeout(() => {
      setCursor((c) => new Date(c.getFullYear(), c.getMonth() + (overId === 'nav:next' ? 1 : -1), 1));
      navTimer.current = null;
      if (lastOverIdRef.current === overId) armNavTimer(overId);
    }, NAV_DWELL_MS);
  }

  function handleDragOver(e: DragOverEvent) {
    const overId = e.over?.id ? String(e.over.id) : null;
    lastOverIdRef.current = overId;
    clearNavTimer();
    if (overId === 'nav:prev' || overId === 'nav:next') {
      armNavTimer(overId);
    }
  }

  // Nettoyage commun de fin de drag (succès ou annulation) : coupe le timer
  // de dwell et sa cible mémorisée, sinon un cycle de réarmement en cours
  // survivrait à un drag qui n'existe plus (chevron qui change de mois en
  // boucle indéfiniment). Réinitialise aussi la tâche active du DragOverlay.
  function resetDragState() {
    lastOverIdRef.current = null;
    clearNavTimer();
    setActiveTask(null);
  }

  function handleDragEnd(e: DragEndEvent) {
    const task = activeTask;
    const overId = e.over?.id ? String(e.over.id) : null;
    resetDragState();
    if (!task || !overId) return;
    if (overId.startsWith('day:')) {
      onReschedule?.(task, overId.slice(4));
    }
    // Déposer sur un chevron n'assigne aucune date.
  }

  // dnd-kit route Escape, ainsi que window blur/resize/visibilitychange,
  // vers onDragCancel — PAS onDragEnd. Sans ce handler, annuler un drag
  // pendant le dwell laissait tourner le timer de réarmement indéfiniment
  // (le mois change toutes les 600 ms sans aucun drag actif).
  function handleDragCancel(_e: DragCancelEvent) {
    resetDragState();
  }

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className="bg-white rounded-xl border border-cime-border overflow-hidden">
        <header className="flex items-center justify-between px-4 py-3 border-b border-cime-border">
          <h2
            className="text-base font-semibold text-cime-text-primary capitalize"
          >
            {monthLabel}
          </h2>
          <div className="flex items-center gap-1">
            <NavChevron id="nav:prev" onClick={goPrev} label="Mois précédent">
              <ChevronLeft className="w-4 h-4" />
            </NavChevron>
            <button
              type="button"
              onClick={goToday}
              className="px-2.5 py-1 rounded text-xs font-medium text-cime-text-muted hover:bg-[#fbfaf7]"
            >
              Aujourd'hui
            </button>
            <NavChevron id="nav:next" onClick={goNext} label="Mois suivant">
              <ChevronRight className="w-4 h-4" />
            </NavChevron>
          </div>
        </header>

        <div className="grid grid-cols-7 border-b border-cime-border bg-[#fbfaf7]">
          {WEEKDAYS.map((d) => (
            <div
              key={d}
              className="px-2 py-2 text-[11px] font-semibold text-cime-text-muted uppercase tracking-[0.08em] text-center"
            >
              {d}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 grid-rows-6 min-h-[480px]">
          {days.map((d, idx) => {
            const inMonth = sameMonth(d, cursor);
            const isToday = sameDay(d, today);
            const iso = isoDate(d);
            const dayTasks = tasksByDate.get(iso) ?? [];

            return (
              <CalendarDayCell
                key={idx}
                iso={iso}
                inMonth={inMonth}
                quickCreateDay={quickCreateDay}
                onOpenQuickCreate={
                  onQuickCreate
                    ? (isoDay) => {
                        setQuickTitle('');
                        setQuickCreateDay(isoDay);
                      }
                    : undefined
                }
                onCloseQuickCreate={() => setQuickCreateDay(null)}
                quickCreateContent={
                  onQuickCreate ? (
                    <PopoverContent align="start" data-cime-modal className="w-64 p-2 rounded-lg border-cime-border bg-white shadow-lg">
                      <p className="text-[11px] font-semibold text-cime-text-muted mb-1.5">
                        Nouvelle tâche pour le{' '}
                        {new Date(`${iso}T00:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}
                      </p>
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          const title = quickTitle.trim();
                          if (!title) return;
                          onQuickCreate?.(iso, title);
                          setQuickCreateDay(null);
                        }}
                      >
                        <input
                          autoFocus
                          value={quickTitle}
                          onChange={(e) => setQuickTitle(e.target.value)}
                          placeholder="Titre de la tâche…"
                          className="w-full px-2 py-1.5 rounded border border-cime-border text-sm outline-none focus:border-cime-primary"
                        />
                      </form>
                    </PopoverContent>
                  ) : undefined
                }
              >
                {isToday ? (
                  <span className="w-5 h-5 rounded-full bg-cime-primary text-white flex items-center justify-center text-[11px] font-semibold">
                    {d.getDate()}
                  </span>
                ) : (
                  <span
                    className={`text-xs font-medium ${
                      inMonth ? 'text-cime-text-primary' : 'text-neutral-400'
                    }`}
                  >
                    {d.getDate()}
                  </span>
                )}

                <div className="flex-1 flex flex-col gap-0.5 overflow-hidden">
                  {dayTasks.slice(0, 3).map((t) => (
                    <CalendarTaskPill
                      key={t.id}
                      task={t}
                      onOpenTask={onOpenTask}
                      draggable={!!onReschedule}
                    />
                  ))}
                  {dayTasks.length > 3 && (
                    <Popover>
                      <PopoverTrigger asChild>
                        <button type="button" className="text-[10px] text-cime-text-muted px-1.5 text-left hover:text-cime-primary">
                          +{dayTasks.length - 3} autres
                        </button>
                      </PopoverTrigger>
                      <PopoverContent align="start" data-cime-modal className="w-auto min-w-[220px] max-h-72 overflow-y-auto p-1 rounded-lg border-cime-border bg-white shadow-lg">
                        {dayTasks.map((t) => (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => onOpenTask?.(t)}
                            data-task-id={t.id}
                            className="flex w-full items-center gap-1.5 px-2 py-1.5 rounded text-xs text-left text-cime-text-primary hover:bg-[#fbfaf7]"
                          >
                            {showPriority && (
                            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${getPriorityMeta(t.priority).dot}`} aria-hidden />
                            )}
                            <span className="truncate">{t.content}</span>
                          </button>
                        ))}
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
              </CalendarDayCell>
            );
          })}
        </div>
      </div>

      <DragOverlay dropAnimation={null}>
        {activeTask && (
          <div className="flex items-center gap-1 text-[11px] leading-tight px-1.5 py-0.5 rounded border bg-white border-cime-primary/40 shadow-lg opacity-90">
            {showPriority && (
            <span
              className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${getPriorityMeta(activeTask.priority).dot}`}
              aria-hidden
            />
            )}
            <span className="truncate">{activeTask.content}</span>
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}

// ============================================================
// Chevron de navigation, droppable pour la navigation au survol
// ============================================================
interface NavChevronProps {
  id: 'nav:prev' | 'nav:next';
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}

function NavChevron({ id, onClick, label, children }: NavChevronProps) {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <button
      ref={setNodeRef}
      type="button"
      onClick={onClick}
      className={`p-1.5 rounded text-cime-text-muted ${
        isOver ? 'bg-cime-primary/10 text-cime-primary' : 'hover:bg-[#fbfaf7]'
      }`}
      aria-label={label}
    >
      {children}
    </button>
  );
}

// ============================================================
// Cellule de jour, droppable pour la replanification
// ============================================================
interface CalendarDayCellProps {
  iso: string;
  inMonth: boolean;
  children: React.ReactNode;
  quickCreateDay: string | null;
  onOpenQuickCreate?: (iso: string) => void;
  onCloseQuickCreate: () => void;
  quickCreateContent?: React.ReactNode;
}

function CalendarDayCell({
  iso,
  inMonth,
  children,
  quickCreateDay,
  onOpenQuickCreate,
  onCloseQuickCreate,
  quickCreateContent,
}: CalendarDayCellProps) {
  const { setNodeRef, isOver } = useDroppable({ id: `day:${iso}` });

  function handleClick(e: React.MouseEvent<HTMLDivElement>) {
    if (!onOpenQuickCreate) return;
    if ((e.target as HTMLElement).closest('button')) return;
    onOpenQuickCreate(iso);
  }

  const cell = (
    <div
      ref={setNodeRef}
      onClick={handleClick}
      className={`border-r border-b border-[#efece3] last:border-r-0 p-1.5 flex flex-col gap-1 ${
        inMonth ? 'bg-white' : 'bg-[#fbfaf7]/60'
      } ${isOver ? 'ring-1 ring-cime-primary/40 bg-cime-primary/5' : ''}`}
    >
      {children}
    </div>
  );

  if (!onOpenQuickCreate) return cell;

  return (
    <Popover open={quickCreateDay === iso} onOpenChange={(o) => !o && onCloseQuickCreate()}>
      <PopoverAnchor asChild>{cell}</PopoverAnchor>
      {quickCreateContent}
    </Popover>
  );
}

// ============================================================
// Pastille de tâche, draggable pour la replanification
// ============================================================
interface CalendarTaskPillProps {
  task: Task;
  onOpenTask?: (task: Task) => void;
  draggable: boolean;
}

function CalendarTaskPill({ task, onOpenTask, draggable }: CalendarTaskPillProps) {
  const { priority: showPriority } = useEffectiveTaskFields();
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: task.id,
    disabled: !draggable,
  });

  const meta = getPriorityMeta(task.priority);
  const urgency = getUrgencyMeta(task);
  const urgentTint =
    urgency.level === 'overdue' || urgency.level === 'critical' || urgency.level === 'warning'
      ? urgency.className
      : 'bg-[#fbfaf7] border-[#efece3] text-cime-text-primary';

  return (
    <button
      ref={setNodeRef}
      type="button"
      onClick={() => onOpenTask?.(task)}
      data-task-id={task.id}
      disabled={!onOpenTask}
      className={`flex items-center gap-1 text-[11px] leading-tight px-1.5 py-0.5 rounded border border-l-2 text-left w-full ${
        task.is_completed ? 'opacity-50' : ''
      } ${urgentTint} ${showPriority ? meta.borderL : ''} ${isDragging ? 'opacity-30' : ''}`}
      title={urgency.label ? `${task.content} — ${urgency.label}` : task.content}
      {...attributes}
      {...listeners}
    >
      <span className="truncate">{task.content}</span>
    </button>
  );
}
